import { isArray, isUndefined, maxBy, mergeWith, minBy, toInteger } from 'lodash';
import { v4 as uuid } from 'uuid';
import { getCenterFromLeftTop, getVectorInSystem } from '@/utils/math-kit';
import {
    LayerType,
    MaterialIdsType,
    type IGroupExtension,
    type IGroupLayerProps,
    type ILayerProps,
    type IPoint,
} from '@/version-types';

/**
 * 合并 MaterialIds
 * @param materialIds - 合并的基对象
 * @param sourceMaterialIds - 合并的目标对象
 * @param targetMaterialIdsType - 仅合并指定类型
 * @returns
 */
export const mergeMaterialIds = (
    materialIds: Record<string, string[]>,
    sourceMaterialIds?: Record<string, string[]>,
    targetMaterialIdsType?: string,
) => {
    if (!sourceMaterialIds) {
        return materialIds;
    }
    const materialIdsObj = materialIds;
    let sourceMaterialIdsObj = sourceMaterialIds;

    if (targetMaterialIdsType) {
        sourceMaterialIdsObj = {
            [targetMaterialIdsType]: (Object.values(sourceMaterialIdsObj) as Array<string[]>).reduce((acc, cur) => {
                return acc.concat(cur);
            }, []),
        };
    }

    const dealFunc = (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
            return [...new Set(objValue.concat(srcValue))];
        }
    };

    mergeWith(materialIdsObj, sourceMaterialIdsObj, dealFunc);

    return materialIdsObj;
};

export const fixLayerBaseInfoToInteger = (layer: ILayerProps) => {
    layer.left = toInteger(layer.left);
    layer.top = toInteger(layer.top);
    layer.width = Math.ceil(+layer.width) || 0;
    layer.height = Math.ceil(+layer.height) || 0;
};

/**
 * 花字素材保留 MaterialId
 * @param layer - 图层属性
 * @param rootLayer - 根图层
 */
export const addAdvancedTextMaterialId = (layer: ILayerProps, rootLayer: ILayerProps | null) => {
    if (!layer.parentId || rootLayer?.layerType !== LayerType.GROUP_LAYER) return;
    if (!(rootLayer.data as IGroupLayerProps).isTextMaterial) return;

    // 获取花字素材ID
    const materialId = (rootLayer.materialIds?.[MaterialIdsType.ADVANCED_TEXT_MATERIAL] || [])[0] || '';

    if (materialId) {
        layer.materialIds = layer.materialIds || {};
        layer.materialIds[MaterialIdsType.ADVANCED_TEXT_MATERIAL] = [materialId];
    }
};

/**
 * 转换素材 ID 为Map
 *
 * - 在 `layersManager` 的 `addOrUpdate` 方法中调用
 *
 * @param layer - 图层属性
 */
export const convertMaterialIdsToMap = (layer: ILayerProps) => {
    fixLayerBaseInfoToInteger(layer);
};

/**
 * 递归转换素材 ID 为 Object
 * @param layers - 图层属性
 */
export const convertMaterialIdsToObjRecursive = (layers: ILayerProps[]) => {
    return layers.forEach((layer) => {
        if (layer.children) convertMaterialIdsToObjRecursive(layer.children);
        convertMaterialIdsToObj(layer);
    });
};

/**
 * 转换素材 ID为 Object
 *
 * - 在 download getResult 方法中调用。
 *
 * @param layer - 图层属性
 */
export const convertMaterialIdsToObj = (layer: ILayerProps) => {
    if (layer.materialIds) {
        Object.keys(layer.materialIds).forEach((key) => {
            if (!layer.materialIds![key] || !isArray(layer.materialIds![key])) {
                return;
            }
            layer.materialIds![key] = layer.materialIds![key].map((item) => {
                if (item) {
                    return item.toString();
                }
                return item;
            });
        });
    }
    fixLayerBaseInfoToInteger(layer);
};

/**
 * 递归转换素材 ID 为 Map
 * 旧代码叫做 convertLayersMaterialIds
 * @param layers - 图层属性
 */
export const convertMaterialIdsToMapRecursive = (layers: ILayerProps[]) => {
    return layers.forEach((layer) => {
        if (layer.children) convertMaterialIdsToMapRecursive(layer.children);
        convertMaterialIdsToMap(layer);
    });
};

/**
 * 克隆图层
 *
 * @param layer - 图层属性
 * @param offsetX - 可选；水平偏移量；默认值为 `0`
 * @param offsetY - 可选；垂直偏移量；默认值为 `0`
 * @param parentId - 可选；父元素 ID
 */
export function resetLayer(layer: ILayerProps, offsetX = 0, offsetY = 0, parentId?: string): ILayerProps {
    layer.id = uuid();
    layer.left = layer.left + offsetX;
    layer.top = layer.top + offsetY;
    layer.parentId = undefined;
    deepDeletePrivateProps(layer, true);
    if (parentId) {
        layer.parentId = parentId;
    }
    (layer.children || []).forEach((item) => {
        resetLayer(item, offsetX, offsetY, layer.id);
    });

    return layer;
}

/**
 * 删除 __ 开头的属性
 *
 * @param obj - 对象
 * @param isClone - 可选；是否是克隆的；默认值为 `false`
 */
export function deepDeletePrivateProps(obj: unknown, isClone = false) {
    if (!obj || typeof obj !== 'object') return;
    // 删除当前层级的 __ 属性
    Object.keys(obj).forEach((key) => {
        if (isClone ? key.startsWith('__render') || key.startsWith('__is') : key.startsWith('__')) {
            delete obj[key];
        } else if (typeof obj[key] === 'object') {
            // 递归处理对象和数组
            deepDeletePrivateProps(obj[key], isClone);
        }
    });
}

export function resetGroupLayerRotate(layer: ILayerProps, positions: IPoint[]) {
    positions = positions.map((point) => {
        return getVectorInSystem(point, layer.rotate);
    });

    // 组的尺寸需要加上 padding
    let paddingTop = 0;
    let paddingRight = 0;
    let paddingBottom = 0;
    let paddingLeft = 0;
    const extension = getGroupExtension<IGroupExtension>(layer);
    if (extension) {
        if (!isUndefined(extension.padding)) {
            if (isArray(extension.padding)) {
                paddingTop = extension.padding[0] ?? 0;
                paddingRight = extension.padding[1] ?? paddingTop;
                paddingBottom = extension.padding[2] ?? paddingTop;
                paddingLeft = extension.padding[3] ?? paddingRight;
            } else {
                paddingTop = extension.padding;
                paddingRight = extension.padding;
                paddingBottom = extension.padding;
                paddingLeft = extension.padding;
            }
        }
    }

    const minX = minBy(positions, (pos) => pos.x) as IPoint;
    const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
    const minY = minBy(positions, (pos) => pos.y) as IPoint;
    const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
    const left = minX.x - paddingLeft;
    const top = minY.y - paddingTop;
    const width = maxX.x - minX.x + paddingLeft + paddingRight;
    const height = maxY.y - minY.y + paddingTop + paddingBottom;

    const center = getCenterFromLeftTop({
        width,
        height,
        leftTop: {
            x: left,
            y: top,
        },
        rotate: 0,
    });

    const trueCenter = getVectorInSystem(center, -layer.rotate);

    layer.left = trueCenter.x - width / 2;
    layer.top = trueCenter.y - height / 2;
    layer.width = width;
    layer.height = height;
}

/**
 * 判断是否是图层
 *
 * @param obj - 对象
 */
export function isLayerProps(obj: unknown): obj is ILayerProps {
    return (
        obj !== null &&
        typeof obj === 'object' &&
        'data' in obj &&
        'id' in obj &&
        'layerType' in obj &&
        'left' in obj &&
        'top' in obj &&
        'width' in obj &&
        'height' in obj &&
        'rotate' in obj
    );
}

/**
 * 获取组图层的扩展信息
 *
 * @param layer - 图层属性
 * @returns 组图层的扩展信息
 */
export function getGroupExtension<T>(layer: ILayerProps): T | null {
    // console.log('getGroupExtension', layer);
    if (layer?.layerType !== LayerType.GROUP_LAYER || !layer.data) return null;
    return (layer.data as IGroupLayerProps).extension as T;
}
